import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import {
  Receipt,
} from '@mui/icons-material';
import { formatCurrency } from '../utils/feeCalculator';

/**
 * MerchantFeePreview Component
 *
 * Displays a live-updating fee preview for merchant configuration.
 * Shows how fees would be calculated for a sample transaction.
 *
 * @param {Object} props
 * @param {number} props.merchantFeePct - Merchant fee percentage
 * @param {number} props.merchantFeeUsd - Merchant flat fee USD
 * @param {number} props.customerFeePct - Customer fee percentage
 * @param {number} props.customerFeeUsd - Customer flat fee USD
 * @param {string} props.feePayer - Who pays: 'merchant', 'customer', or 'shared'
 * @param {number} [props.transactionAmount=100] - Example transaction amount
 */
export function MerchantFeePreview({
  merchantFeePct = 0,
  merchantFeeUsd = 0,
  customerFeePct = 0,
  customerFeeUsd = 0,
  feePayer = 'merchant',
  transactionAmount = 100,
}) {
  const txAmount = parseFloat(transactionAmount) || 100;
  const mPct = parseFloat(merchantFeePct) || 0;
  const mFlat = parseFloat(merchantFeeUsd) || 0;
  const cPct = parseFloat(customerFeePct) || 0;
  const cFlat = parseFloat(customerFeeUsd) || 0;

  // Calculate fees based on who pays
  let merchantFee = 0;
  let customerFee = 0;

  if (feePayer === 'merchant') {
    merchantFee = (txAmount * mPct / 100) + mFlat;
  } else if (feePayer === 'customer') {
    customerFee = (txAmount * cPct / 100) + cFlat;
  } else {
    // Shared
    merchantFee = (txAmount * mPct / 100) + mFlat;
    customerFee = (txAmount * cPct / 100) + cFlat;
  }

  const totalFees = merchantFee + customerFee;
  const customerPays = txAmount + customerFee;
  const merchantReceives = txAmount - merchantFee;

  // Round to 2 decimals
  const round2 = (v) => Math.round(v * 100) / 100;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mt: 3,
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
          }}
        >
          <Receipt sx={{ fontSize: 18 }} />
        </Box>
        <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#1e293b' }}>
          Fee Preview
        </Typography>
        <Chip
          label={`$${txAmount} sale`}
          size="small"
          sx={{
            ml: 'auto',
            backgroundColor: '#fef3c7',
            color: '#92400e',
            fontWeight: 500,
            fontSize: '12px',
          }}
        />
      </Box>

      {/* Fee breakdown */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ fontSize: '13px', color: '#475569' }}>Sale Amount</Typography>
          <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#1e293b' }}>
            {formatCurrency(txAmount)}
          </Typography>
        </Box>

        {merchantFee > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography sx={{ fontSize: '13px', color: '#475569' }}>
              Merchant Fee {mPct > 0 && mFlat > 0 ? `(${mPct}% + $${mFlat})` : mPct > 0 ? `(${mPct}%)` : `($${mFlat})`}
            </Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#dc2626' }}>
              -{formatCurrency(round2(merchantFee))}
            </Typography>
          </Box>
        )}

        {customerFee > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography sx={{ fontSize: '13px', color: '#475569' }}>
              Customer Fee {cPct > 0 && cFlat > 0 ? `(${cPct}% + $${cFlat})` : cPct > 0 ? `(${cPct}%)` : `($${cFlat})`}
            </Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#059669' }}>
              +{formatCurrency(round2(customerFee))}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, mt: 1, borderTop: '1px dashed #e2e8f0' }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>Total Fees</Typography>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
            {formatCurrency(round2(totalFees))}
          </Typography>
        </Box>
      </Box>

      {/* Summary */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          pt: 1.5,
          borderTop: '1px solid #e2e8f0',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>
            Customer Pays
          </Typography>
          <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>
            {formatCurrency(round2(customerPays))}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>
            Merchant Receives
          </Typography>
          <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#059669' }}>
            {formatCurrency(round2(merchantReceives))}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

export default MerchantFeePreview;
